import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import IdentityAvatarContainer from '../../../../containers/global/identity/IdentityAvatarContainer';
import IdentityNameContainer from '../../../../containers/global/identity/IdentityNameContainer';
import { UPDATE_ACCOUNT_IDENTITY_MODAL } from '../../constants/modals';
import { AVATAR_SIZE_52 } from '../../../../constants/avatar-sizes';
import EditIcon from '../../../../components/icons/EditIcon';
import Badge from '../../../../components/global/Badge';

const propTypes = {
  identity: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    relationship: PropTypes.string,
    current_photo: PropTypes.object,
    has_outdated_selfie: PropTypes.bool,
  }),
  uploadedPhoto: PropTypes.object,
  openModal: PropTypes.func.isRequired,
  openAlert: PropTypes.func.isRequired,
};

const SelfieSwapIdentity = ({
  identity,
  uploadedPhoto,
  openModal,
  openAlert,
}) => {
  const [uploadedAt, setUploadedAt] = useState(null);

  const requiresSelfieUpdate = identity.has_outdated_selfie && !uploadedPhoto;
  const missingSelfie = !identity.current_photo && !uploadedPhoto;
  const isValidSelfie = !requiresSelfieUpdate && !missingSelfie;
  const isRecentSelfie = !!uploadedPhoto;
  const lastJoinedItemName =
    identity.selected_identities?.records?.[0]?.album_membership?.album
      ?.subscription_album_folder?.name ??
    identity.selected_identities?.records?.[0]?.album_membership?.album?.name;

  useEffect(() => {
    if (uploadedPhoto) {
      setUploadedAt(new Date());
      // Display success alert:
      openAlert({
        type: 'success',
        title: 'Selfie photo updated!',
        message: (
          <Fragment>
            <p>
              The selfie photo for{' '}
              <IdentityNameContainer
                className="text-dark text-bold"
                identity={identity}
              />{' '}
              has been properly updated.
            </p>
            <IdentityAvatarContainer
              key={identity.id}
              identity={identity}
              className="my-1"
              size={AVATAR_SIZE_52}
            />
          </Fragment>
        ),
      });
    }
  }, [uploadedPhoto]);

  const onIdentitySelected = event => {
    event.preventDefault();

    if (!identity.current_photo && !uploadedPhoto) {
      openModal(UPDATE_ACCOUNT_IDENTITY_MODAL, {
        identity,
        forceSelfieUpdate: true,
      });
      return;
    }

    // Taken from SelectAccountIdentity.js
    // `updateSelfie` request is not sync in nature, i.e.
    // the field identity.has_outdated_selfie will return `true`
    // even if we update the selfie, so to check if a user
    // really has an outdated selfie we also need to check
    // our local redux store for recent uploads for this identity,
    // if there's an `uploadedPhoto` it means that the user just
    // uploaded a selfie (which by definition is not considered outdated)
    if (identity.has_outdated_selfie && !uploadedPhoto) {
      openModal(UPDATE_ACCOUNT_IDENTITY_MODAL, {
        identity,
        outdatedSelfie: true,
      });

      return;
    }

    openModal(UPDATE_ACCOUNT_IDENTITY_MODAL, {
      identity,
    });
  };

  return (
    <a
      href="#"
      className="selfie-swap-identity"
      onClick={onIdentitySelected}
      key={identity.id}
    >
      <div className="identity-info-container">
        <IdentityAvatarContainer
          key={identity.id}
          identity={identity}
          className="mr-2"
          size={AVATAR_SIZE_52}
        >
          <span className="btn-identity-edit">
            <EditIcon color="#0d84fe" />
          </span>
        </IdentityAvatarContainer>

        <IdentityNameContainer
          className="text-dark text-truncate"
          identity={identity}
        />
      </div>

      <div className="identity-selfie-status">
        {requiresSelfieUpdate && (
          <Badge badgeSize="xs" badgeStyle="warning">
            Requires Update
          </Badge>
        )}
        {missingSelfie && (
          <Badge badgeSize="xs" badgeStyle="danger">
            Missing Selfie
          </Badge>
        )}
        {isValidSelfie && (
          <Badge
            badgeSize="xs"
            badgeStyle={isRecentSelfie ? 'success' : 'info'}
          >
            Uploaded on{' '}
            {moment(uploadedAt ?? identity.current_photo?.created_at).format(
              'MM/DD/YY',
            )}
          </Badge>
        )}

        {lastJoinedItemName && (
          <p className="text-xs mb-0 text-dark mt-1 text-truncate">
            Last Join: {lastJoinedItemName}
          </p>
        )}
      </div>
    </a>
  );
};

SelfieSwapIdentity.propTypes = propTypes;

export default SelfieSwapIdentity;
